/**
 * Student portal — a first-person, growth-framed lens on ONE learner's
 * comprehension fingerprint. The job: "what do I do next?" Everything is derived
 * from the same roster the teacher dashboard reads (roleProjections.ts), re-voiced
 * for the learner. No peer comparison (minor-safe).
 *
 * Interactive like the teacher portal: topic cards / next-moves / fingerprint tags
 * drill into a per-topic detail (internal view state, back nav), and "Replay lesson"
 * opens the real interactive video (/learn/:shareToken).
 */

import { useState } from 'react';
import {
  ArrowLeft, Sparkles, Target, Compass, Zap, Clock, Repeat, TrendingUp, Flame, Award,
  AlertTriangle, Heart, Play, RotateCcw, ArrowRight, CheckCircle2, Eye, Flag, ChevronRight, BookOpen,
} from 'lucide-react';
import type { ClassRoster, Learner } from './mockData';
import { CONCEPTS, TOPICS, attemptOf, conceptsOfTopic, conceptMasteryOf, conceptLabel } from './mockData';
import { MASTERY_META, Donut, Radar, Pill } from './charts';
import { PaceStrip, TrendLine, LoopStepper } from './portalCharts';
import { useOpenLesson, useLocalFlag } from './portalHooks';
import {
  masteredConcepts, developingConcepts, focusConcepts, flaggedConcepts,
  studentMoves, masteryTrend, pace, loopStage, lastAttempt, reviewItems, weeklyActivity, plainMisconception,
} from './roleProjections';

const SHORT: Record<string, string> = {
  electrostatics: 'Electro', 'current-electricity': 'Current', 'ray-optics': 'Optics', 'em-waves': 'EM Waves',
};
const daysAgo = (n: number) => (n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`);
const topicOfTag = (tag: string) => TOPICS.find((t) => t.conceptTags.includes(tag))?.id;
const SEV = {
  high: { t: 'Do first', c: '#CB5B6B', icon: Target },
  medium: { t: 'Recommended', c: '#C99A3B', icon: Compass },
  low: { t: 'Opportunity', c: '#2F8F80', icon: Zap },
} as const;

export function StudentPortal({ roster, learner, onBack }: { roster: ClassRoster; learner: Learner; onBack?: () => void }) {
  const [topicId, setTopicId] = useState<string | null>(null);
  const openLesson = useOpenLesson();
  const [goalSet, toggleGoal] = useLocalFlag(`learn-goal-${learner.id}`);

  if (topicId) {
    return <StudentTopicDetail roster={roster} learner={learner} topicId={topicId} onBack={() => setTopicId(null)} openLesson={openLesson} />;
  }

  const mastered = masteredConcepts(learner);
  const developing = developingConcepts(learner);
  const focus = focusConcepts(learner);
  const flagged = flaggedConcepts(learner);
  const moves = studentMoves(learner);
  const trend = masteryTrend(learner);
  const act = weeklyActivity(learner);
  const last = lastAttempt(learner);
  const heroColor = learner.overallMasteryPercent >= 70 ? '#2F8F80' : learner.overallMasteryPercent >= 55 ? '#C99A3B' : '#CB5B6B';
  const radarAxes = TOPICS.map((t) => ({ label: SHORT[t.id] ?? t.title, value: (attemptOf(learner, t.id)?.scorePercent ?? 0) / 100 }));

  return (
    <div className="pt-2">
      {onBack && (
        <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
          <ArrowLeft className="h-4 w-4" /> Back to class
        </button>
      )}

      <div className="mb-4 flex items-center gap-2.5 text-[14px] text-[#56505F] dark:text-[#B2B0C0]">
        <Sparkles className="h-4 w-4 text-[#6D28D9] dark:text-[#B69BF5]" />
        <span><b className="font-semibold text-[#1B1826] dark:text-white">My Learning</b> · what you have mastered, and exactly what to do next.</span>
      </div>

      {/* HERO */}
      <div className="flex flex-wrap items-center gap-6 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <Donut percent={learner.overallMasteryPercent} color={heroColor} size={118} caption="mastery" />
        <div className="min-w-[230px] flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#8A8593]">{roster.name} · Physics</div>
          <h1 className="mt-1 font-serif text-[30px] font-medium tracking-tight">Great work, {learner.firstName}.</h1>
          <p className="mt-1 text-[15px] text-[#56505F] dark:text-[#B2B0C0]">
            You have mastered <b className="text-[#1B1826] dark:text-white">{mastered.length} of {CONCEPTS.length}</b> concepts.
            {focus.length + developing.length > 0 ? <> Just <b className="text-[#1B1826] dark:text-white">{focus.length + developing.length}</b> to go, and you already know which ones.</> : ' A clean sweep.'}
          </p>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {act.streak > 0 && <Pill color="#C99A3B"><Flame className="h-3 w-3" />{act.streak}-day streak</Pill>}
            <Pill><Award className="h-3 w-3" />{mastered.length} mastered</Pill>
            <Pill><Clock className="h-3 w-3" />Studied {daysAgo(learner.lastActiveDaysAgo)}</Pill>
          </div>
        </div>
        <div className="text-center">
          <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]">
            <TrendingUp className="h-3.5 w-3.5" /> Concepts mastered, over the term
          </div>
          <div className="w-[320px] max-w-full"><TrendLine points={trend} max={CONCEPTS.length} /></div>
        </div>
      </div>

      {/* NEXT MOVES */}
      <SectionHead title="Your next moves" sub="Ranked by what will move your mastery most, grounded in exactly what you got wrong. Tap any move to dive in." />
      <div className="flex flex-col gap-4">
        {moves.map((m, i) => {
          const meta = SEV[m.severity];
          const Icon = meta.icon;
          const canDrill = !!m.topicId;
          return (
            <div key={i} role={canDrill ? 'button' : undefined} tabIndex={canDrill ? 0 : undefined}
              onClick={canDrill ? () => setTopicId(m.topicId!) : undefined}
              onKeyDown={canDrill ? (e) => (e.key === 'Enter' || e.key === ' ') && setTopicId(m.topicId!) : undefined}
              className={`rounded-2xl border border-black/10 bg-black/[0.015] p-[18px] transition-colors dark:border-white/10 dark:bg-white/[0.02] ${canDrill ? 'cursor-pointer hover:bg-black/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 dark:hover:bg-white/[0.05]' : ''}`}
              style={{ borderLeft: `3px solid ${meta.c}` }}>
              <div className="flex flex-wrap items-center justify-between gap-2.5">
                <h4 className="flex items-center gap-2 font-serif text-[15px] font-semibold">
                  <Icon className="h-4 w-4" style={{ color: meta.c }} />{m.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: `${meta.c}1f`, color: meta.c }}>{meta.t}</span>
                  <Pill><CheckCircle2 className="h-3 w-3" />{m.steps}</Pill>
                </div>
              </div>
              <p className="mt-2 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{m.body}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StudentBtn primary={m.severity === 'high'} onClick={(e) => { e.stopPropagation(); openLesson(m.topicId ?? TOPICS[0].id); }}><Play className="h-3.5 w-3.5" />Replay lesson</StudentBtn>
                {m.kind !== 'stretch'
                  ? <StudentBtn onClick={(e) => { e.stopPropagation(); openLesson(m.topicId ?? TOPICS[0].id); }}><RotateCcw className="h-3.5 w-3.5" />Retake questions</StudentBtn>
                  : <StudentBtn onClick={(e) => { e.stopPropagation(); openLesson(TOPICS[0].id); }}><ArrowRight className="h-3.5 w-3.5" />Open challenge</StudentBtn>}
                {i === 0 && (
                  <button onClick={(e) => { e.stopPropagation(); toggleGoal(); }}
                    className={`ml-auto inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-2 text-[12.5px] font-semibold transition-colors ${goalSet ? 'border-transparent bg-[#2F8F80]/15 text-[#1F6B5F] dark:text-[#5FC7B6]' : 'border-black/10 text-[#56505F] hover:text-[#1B1826] dark:border-white/10 dark:text-[#B2B0C0] dark:hover:text-white'}`}>
                    {goalSet ? <><CheckCircle2 className="h-3.5 w-3.5" />This week&rsquo;s goal</> : <><Flag className="h-3.5 w-3.5" />Make this my goal</>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* FINGERPRINT */}
      <SectionHead title="Your comprehension fingerprint" sub="Where you are strong, and where to aim next. Tap a focus concept to work on it." />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.25fr_1fr]">
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="flex flex-wrap items-center gap-6">
            <div className="h-[210px] w-[210px] shrink-0 text-[#6D28D9] dark:text-[#B69BF5]"><Radar axes={radarAxes} /></div>
            <div className="min-w-[170px] flex-1 space-y-4">
              <FingerCol title="Strengths" tags={mastered.slice(0, 8).map((c) => c.conceptTag)} state="mastered" empty="No mastered concepts yet" />
              <FingerCol title="Developing" tags={developing.map((c) => c.conceptTag)} state="developing" empty="Nothing in progress" onTag={(tag) => setTopicId(topicOfTag(tag) ?? null)} />
              <FingerCol title="Focus next" tags={focus.map((c) => c.conceptTag)} state="emerging" empty="No open gaps" onTag={(tag) => setTopicId(topicOfTag(tag) ?? null)} />
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="mb-3 inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]"><Sparkles className="h-3.5 w-3.5" />Traps to unlearn</div>
          <div className="flex flex-col gap-2.5">
            {flagged.map((c) => (
              <button key={c.conceptTag} onClick={() => setTopicId(topicOfTag(c.conceptTag) ?? null)}
                className="flex gap-3 rounded-xl border border-black/10 bg-black/[0.015] p-[14px] text-left transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-white/[0.05]">
                <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-[#CB5B6B]/16 text-[#B23B4E]"><AlertTriangle className="h-[18px] w-[18px]" /></span>
                <div className="flex-1"><h5 className="mb-0.5 flex items-center gap-1 text-[13px] font-bold">{conceptLabel(c.conceptTag)}<ChevronRight className="h-3.5 w-3.5 text-[#8A8593]" /></h5>
                  <p className="text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">{c.lastMisconceptionTag ? plainMisconception(c.lastMisconceptionTag, 'student') : ''}</p></div>
              </button>
            ))}
            <div className="flex gap-3 rounded-xl border border-black/10 bg-black/[0.015] p-[14px] dark:border-white/10 dark:bg-white/[0.02]">
              <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-[#2F8F80]/16 text-[#1F6B5F] dark:text-[#5FC7B6]"><Heart className="h-[18px] w-[18px]" /></span>
              <div><h5 className="mb-0.5 text-[13px] font-bold">The good news</h5><p className="text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">These are single, nameable ideas, the fastest kind of gap to close. Name it once and it usually clicks.</p></div>
            </div>
            {flagged.length === 0 && <p className="text-[12.5px] text-[#8A8593]">No misconceptions flagged, you are reasoning cleanly.</p>}
          </div>
        </div>
      </div>

      {/* HOW YOU'RE WORKING */}
      {last && (
        <>
          <SectionHead title="How you're working" sub="Not just what you got right, but how you got there. This lens is yours alone." />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
              {(() => { const p = pace(last); return (
                <>
                  <div className="mb-3 inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]"><Clock className="h-3.5 w-3.5" />Pace &amp; confidence</div>
                  <div className="flex flex-wrap gap-3.5">
                    <PaceStat sec={p.correctSec} label="right" tone="good" />
                    <PaceStat sec={p.missedSec} label="missed" tone="bad" />
                  </div>
                  <p className="mt-3.5 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{p.insight}</p>
                  <div className="mt-3.5 mb-1.5 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]">Every question, by time spent</div>
                  <PaceStrip dots={p.strip} />
                  <div className="mt-1 flex gap-4 text-[11px] text-[#8A8593]">
                    <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-[#2F8F80]" />correct</span>
                    <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-[#CB5B6B]" />missed</span>
                  </div>
                </>
              ); })()}
            </div>
            <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="mb-3.5 inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]"><Repeat className="h-3.5 w-3.5" />Your improvement loop</div>
              <LoopStepper at={loopStage(learner)} />
              <div className="mt-[18px] rounded-xl border border-black/10 bg-[#2F8F80]/[0.10] p-[14px] dark:border-white/10">
                <div className="flex items-center gap-2 text-[13px] font-bold text-[#1F6B5F] dark:text-[#5FC7B6]"><TrendingUp className="h-4 w-4" />You are one step from a jump</div>
                <p className="mt-1.5 text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">
                  You have already replayed {last.replays > 0 ? `${TOPICS.find((t) => t.id === last.topicId)?.title} ${last.replays} times` : 'the lessons'}. Reviewing your misses and retaking usually adds about <b className="text-[#1B1826] dark:text-white">+12 points</b>. You have done the hard part, now claim it.
                </p>
                <StudentBtn teal onClick={() => openLesson(last.topicId)}><RotateCcw className="h-3.5 w-3.5" />Retake {TOPICS.find((t) => t.id === last.topicId)?.title}</StudentBtn>
              </div>
            </div>
          </div>
        </>
      )}

      {/* TOPIC MAP */}
      <SectionHead title="Every topic, every concept" sub="Tap a topic to see the detail, or jump straight back into the lesson." />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {TOPICS.map((t) => {
          const a = attemptOf(learner, t.id);
          const sc = a?.scorePercent ?? 0;
          const col = sc >= 75 ? '#1F6B5F' : sc >= 55 ? '#9A7320' : '#B23B4E';
          return (
            <div key={t.id} role="button" tabIndex={0} onClick={() => setTopicId(t.id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setTopicId(t.id)}
              className="cursor-pointer rounded-2xl border border-black/10 bg-white p-[18px] transition-colors hover:border-[#7C3AED]/30 hover:bg-black/[0.01] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.06]">
              <div className="mb-3 flex items-start justify-between gap-2.5">
                <div>
                  <h3 className="flex items-center gap-1 font-serif text-[15px] font-semibold">{t.title}<ChevronRight className="h-4 w-4 text-[#8A8593]" /></h3>
                  {a && (
                    <div className="mt-1 flex gap-3 text-[11.5px] tabular-nums text-[#8A8593]">
                      <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{a.watchPercent}% watched</span>
                      <span className="inline-flex items-center gap-1"><Repeat className="h-3 w-3" />{a.replays} replays</span>
                    </div>
                  )}
                </div>
                <span className="font-serif text-[19px] font-semibold tabular-nums" style={{ color: a ? col : '#8A8593' }}>{a ? `${sc}%` : '—'}</span>
              </div>
              <ConceptBars topicId={t.id} learner={learner} />
              <div className="mt-3.5 flex gap-2">
                <StudentBtn small onClick={(e) => { e.stopPropagation(); openLesson(t.id); }}><Play className="h-3.5 w-3.5" />Replay</StudentBtn>
                <StudentBtn small onClick={(e) => { e.stopPropagation(); openLesson(t.id); }}><RotateCcw className="h-3.5 w-3.5" />Retake</StudentBtn>
              </div>
            </div>
          );
        })}
      </div>

      {/* LAST TEST */}
      {last && (() => {
        const topic = TOPICS.find((t) => t.id === last.topicId)!;
        const items = reviewItems(learner, last);
        return (
          <>
            <SectionHead title={`Your last test · ${topic.title}`} sub={`Taken ${daysAgo(last.submittedDaysAgo)}. Only your first answer counts, but you can retake to practise as often as you like.`} />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.25fr_1fr]">
              <div className="flex flex-col gap-4 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
                <div className="flex flex-wrap items-center gap-5">
                  <Donut percent={last.scorePercent} color="#C99A3B" size={112} label={`${last.correct}/${last.total}`} caption="you scored" />
                  <div className="min-w-[160px] flex-1">
                    <h3 className="font-serif text-[17px] font-semibold">{last.scorePercent >= 70 ? 'Strong, keep it rolling' : items.length ? 'Solid, with a clear thing to fix' : 'A good run'}</h3>
                    <p className="mt-1.5 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{items.length ? `Most of what you missed traces back to ${items[0].conceptLabel}. Fix that and this jumps up.` : 'No repeated gaps, nicely done.'}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StudentBtn teal onClick={() => openLesson(topic.id)}><RotateCcw className="h-3.5 w-3.5" />Retake the test</StudentBtn>
                      <StudentBtn onClick={() => setTopicId(topic.id)}><BookOpen className="h-3.5 w-3.5" />Open topic detail</StudentBtn>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="mb-2.5 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]">Mastery by NCERT concept</div>
                  <ConceptBars topicId={topic.id} learner={learner} />
                </div>
              </div>
              <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
                <div className="mb-3 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]">What to review, and why</div>
                {items.length === 0 && <p className="text-[12.5px] text-[#8A8593]">Nothing to revisit, you got everything on the first pass.</p>}
                {items.map((it) => (
                  <ReviewRow key={it.conceptTag} conceptLabel={it.conceptLabel} missed={it.missed} total={it.total} reason={it.reason} />
                ))}
              </div>
            </div>
          </>
        );
      })()}

      <div className="mt-8 flex items-start gap-3.5 rounded-2xl border border-black/10 bg-white p-5 text-[13px] leading-relaxed text-[#56505F] dark:border-white/10 dark:bg-white/[0.04] dark:text-[#B2B0C0]">
        <Heart className="mt-0.5 h-5 w-5 shrink-0 text-[#1F6B5F] dark:text-[#5FC7B6]" />
        <div><b className="text-[#1B1826] dark:text-white">Your progress, only yours.</b> This view is built from your work inside each lesson and test. We keep your first name and roll number only. Your teacher and parent can see this progress, and this platform uses AI to personalise what you practise next.</div>
      </div>
    </div>
  );
}

// ---- topic drill-in ----

function StudentTopicDetail({ roster, learner, topicId, onBack, openLesson }: {
  roster: ClassRoster; learner: Learner; topicId: string; onBack: () => void; openLesson: (t: string) => void;
}) {
  const topic = TOPICS.find((t) => t.id === topicId)!;
  const a = attemptOf(learner, topicId);
  const items = a ? reviewItems(learner, a) : [];
  const traps = conceptsOfTopic(topicId)
    .map((c) => conceptMasteryOf(learner, c.tag))
    .filter((m): m is NonNullable<typeof m> => !!m && !!m.lastMisconceptionTag && m.state !== 'mastered');
  const sc = a?.scorePercent ?? 0;
  const col = sc >= 70 ? '#2F8F80' : sc >= 55 ? '#C99A3B' : '#CB5B6B';

  return (
    <div className="pt-2">
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
        <ArrowLeft className="h-4 w-4" /> Back to My Learning
      </button>

      <div className="flex flex-wrap items-center gap-6 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        {a && <Donut percent={sc} color={col} size={104} caption="your score" />}
        <div className="min-w-[220px] flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#8A8593]">{roster.name} · Physics</div>
          <h1 className="mt-1 font-serif text-[26px] font-medium tracking-tight">{topic.title}</h1>
          {a ? (
            <div className="mt-2 flex flex-wrap gap-2">
              <Pill><CheckCircle2 className="h-3 w-3" />{a.correct}/{a.total} correct</Pill>
              <Pill><Eye className="h-3 w-3" />{a.watchPercent}% watched</Pill>
              <Pill><Repeat className="h-3 w-3" />{a.replays} replays</Pill>
            </div>
          ) : <p className="mt-1 text-[13px] text-[#8A8593]">You have not started this topic yet.</p>}
        </div>
        <div className="flex flex-col gap-2">
          <StudentBtn primary onClick={() => openLesson(topicId)}><Play className="h-3.5 w-3.5" />Replay the lesson</StudentBtn>
          <StudentBtn onClick={() => openLesson(topicId)}><RotateCcw className="h-3.5 w-3.5" />Retake the test</StudentBtn>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h3 className="mb-4 font-serif text-[16px] font-semibold">Concept by concept</h3>
          <ConceptBars topicId={topicId} learner={learner} />
        </div>
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="mb-3 inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]"><AlertTriangle className="h-3.5 w-3.5" />Traps in this topic</div>
          {traps.length === 0 && <p className="text-[12.5px] text-[#8A8593]">No misconception flagged here, your reasoning is clean.</p>}
          <div className="flex flex-col gap-2.5">
            {traps.map((m) => (
              <div key={m.conceptTag} className="flex gap-3 rounded-xl border border-black/10 bg-black/[0.015] p-[14px] dark:border-white/10 dark:bg-white/[0.02]">
                <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-[#CB5B6B]/16 text-[#B23B4E]"><AlertTriangle className="h-[18px] w-[18px]" /></span>
                <div><h5 className="mb-0.5 text-[13px] font-bold">{conceptLabel(m.conceptTag)}</h5><p className="text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">{plainMisconception(m.lastMisconceptionTag!, 'student')}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-4 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="mb-3 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]">What to review, and why</div>
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            {items.map((it) => <ReviewRow key={it.conceptTag} conceptLabel={it.conceptLabel} missed={it.missed} total={it.total} reason={it.reason} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- local bits ----

function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-4 mt-8">
      <h2 className="font-serif text-[21px] font-semibold tracking-tight">{title}</h2>
      <p className="mt-0.5 text-[13.5px] text-[#8A8593]">{sub}</p>
    </div>
  );
}

function StudentBtn({ children, primary, teal, small, onClick }: { children: React.ReactNode; primary?: boolean; teal?: boolean; small?: boolean; onClick?: (e: React.MouseEvent) => void }) {
  const base = `inline-flex items-center gap-1.5 rounded-[10px] border font-semibold transition-colors ${small ? 'px-2.5 py-1.5 text-[12.5px]' : 'px-3.5 py-2 text-[12.5px]'}`;
  const skin = primary
    ? 'border-transparent bg-[#6D28D9] text-white hover:bg-[#7C3AED]'
    : teal
      ? 'mt-3 border-transparent bg-[#2F8F80] text-white hover:bg-[#268073]'
      : 'border-black/10 bg-white text-[#1B1826] hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.06] dark:text-white';
  return <button onClick={onClick} className={`${base} ${skin}`}>{children}</button>;
}

function PaceStat({ sec, label, tone }: { sec: number; label: string; tone: 'good' | 'bad' }) {
  return (
    <div className="min-w-[120px] flex-1 rounded-xl border border-black/10 bg-black/[0.015] p-[13px] dark:border-white/10 dark:bg-white/[0.02]">
      <div className={`font-serif text-[26px] font-medium tabular-nums ${tone === 'good' ? 'text-[#1F6B5F] dark:text-[#5FC7B6]' : 'text-[#B23B4E] dark:text-[#E79AA6]'}`}>{sec}s</div>
      <div className="mt-0.5 text-[11.5px] text-[#8A8593]">avg on answers you got <b className="text-[#1B1826] dark:text-white">{label}</b></div>
    </div>
  );
}

function ReviewRow({ conceptLabel, missed, total, reason }: { conceptLabel: string; missed: number; total: number; reason?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-black/10 bg-black/[0.015] p-3.5 dark:border-white/10 dark:bg-white/[0.02]">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 text-left text-[13px]">
        <span className="font-semibold">{conceptLabel}</span>
        <span className="flex items-center gap-2"><span className="tabular-nums text-[#B23B4E]">missed {missed}/{total}</span>{reason && <ChevronRight className={`h-4 w-4 text-[#8A8593] transition-transform ${open ? 'rotate-90' : ''}`} />}</span>
      </button>
      {reason && open && <p className="mt-2 border-l-2 border-black/10 pl-3 text-[12.5px] text-[#56505F] dark:border-white/10 dark:text-[#B2B0C0]">{reason}</p>}
    </div>
  );
}

function FingerCol({ title, tags, state, empty, onTag }: { title: string; tags: string[]; state: 'mastered' | 'developing' | 'emerging'; empty: string; onTag?: (tag: string) => void }) {
  const meta = MASTERY_META[state];
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: meta.text }}>
        <i className="h-2 w-2 rounded-full" style={{ background: meta.color }} /> {title}
      </div>
      {tags.length === 0 ? <p className="text-[12px] text-[#8A8593]">{empty}</p> : (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => onTag ? (
            <button key={tag} onClick={() => onTag(tag)} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11.5px] font-medium transition-transform hover:scale-[1.03]" style={{ color: meta.text, borderColor: `${meta.color}40`, background: `${meta.color}12` }}>{conceptLabel(tag)}<ChevronRight className="h-3 w-3 opacity-60" /></button>
          ) : (
            <span key={tag} className="rounded-md border px-2 py-0.5 text-[11.5px] font-medium" style={{ color: meta.text, borderColor: `${meta.color}40`, background: `${meta.color}12` }}>{conceptLabel(tag)}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function ConceptBars({ topicId, learner }: { topicId: string; learner: Learner }) {
  return (
    <div className="space-y-2">
      {conceptsOfTopic(topicId).map((c) => {
        const m = conceptMasteryOf(learner, c.tag);
        if (!m) return null;
        const meta = MASTERY_META[m.state];
        const pct = m.total ? Math.round((m.correct / m.total) * 100) : 0;
        return (
          <div key={c.tag}>
            <div className="mb-1 flex items-center justify-between text-[12.5px]">
              <span className="flex items-center gap-1.5">{c.label}{m.lastMisconceptionTag && <AlertTriangle className="h-3 w-3 text-[#CB5B6B]" />}</span>
              <span className="tabular-nums text-[#8A8593]">{m.total ? `${m.correct}/${m.total}` : 'not started'}</span>
            </div>
            <div className="h-[7px] w-full overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: meta.color, opacity: m.state === 'not-attempted' ? 0.3 : 1 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
